import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Add as AddIcon,
  PlayCircle as PlayCircleIcon,
  LibraryAdd as LibraryAddIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import customFetch from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'react-toastify';
import PageHeader from '../components/common/PageHeader';

const SongCard = ({ song, onAdd, adding, canAdd }) => {
  const theme = useTheme();

  const youtubeUrl = song.youtubeUrl || 
    (Array.isArray(song.streamingLinks) 
      ? song.streamingLinks.find(l => l.type === 'youtube' || l.platform === 'youtube')?.url 
      : null);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.divider,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1rem' }} noWrap>
          {song.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} noWrap>
          {song.artist || 'Unknown Artist'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {song.key && (
            <Chip label={`Key: ${song.key}`} size="small" color="primary" variant="outlined" />
          )}
          {song.tempo && (
            <Chip label={`${song.tempo} BPM`} size="small" variant="outlined" />
          )}
        </Box>
      </CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pb: 1.5 }}>
        {youtubeUrl ? (
          <Tooltip title="Play on YouTube">
            <IconButton
              size="small"
              color="error"
              onClick={() => window.open(youtubeUrl, '_blank')}
            >
              <PlayCircleIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Box />
        )}
        {canAdd && (
          <Button
            size="small"
            variant="contained"
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <LibraryAddIcon />}
            onClick={() => onAdd(song)}
            disabled={adding}
            sx={{ textTransform: 'none' }}
          >
            Add to Library
          </Button>
        )}
      </Box>
    </Card>
  );
};

const SongSuggestions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [trendy, setTrendy] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  const [pendingSongData, setPendingSongData] = useState(null);
  const [futureServices, setFutureServices] = useState([]);

  const canAdd = user && (user.role === 'admin' || user.role === 'leader');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [trendyRes, recommendedRes] = await Promise.all([
        customFetch.get(`${API_ENDPOINTS.SONG_SUGGESTIONS.TRENDY}?limit=10`),
        customFetch.get(`${API_ENDPOINTS.SONG_SUGGESTIONS.RECOMMENDED}?limit=10`),
      ]);
      setTrendy(trendyRes.data?.data || []);
      setRecommended(recommendedRes.data?.data || []);
    } catch (err) {
      console.error('Error loading song suggestions:', err);
      setError('Failed to load song suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddToLibrary = async (song) => {
    setAddingId(song._id);
    try {
      const songData = {
        title: song.title,
        artist: song.artist,
        key: song.key,
        tempo: song.tempo,
        chordChart: song.chordChart,
        youtubeUrl: song.youtubeUrl,
      };

      // Fetch future services
      const servicesRes = await customFetch.get(API_ENDPOINTS.SERVICES.BASE);
      const allServices = servicesRes.data?.data || servicesRes.data || [];
      const upcoming = Array.isArray(allServices)
        ? allServices.filter(s => new Date(s.date) >= new Date()).slice(0, 10)
        : [];

      if (upcoming.length > 0) {
        setPendingSongData(songData);
        setFutureServices(upcoming);
        setServicePickerOpen(true);
      } else {
        // No future services, just add to library
        const result = await customFetch.post(API_ENDPOINTS.SONG_SUGGESTIONS.ADD_TO_LIBRARY, { songData });
        toast.success(
          result.data?.isNew
            ? t('songSuggestions.addedNew', 'Song added to your library!')
            : t('songSuggestions.alreadyExists', 'Song was already in your library.')
        );
      }
    } catch (err) {
      console.error('Error adding song:', err);
      toast.error('Failed to add song to library');
    } finally {
      setAddingId(null);
    }
  };

  const handleAddToLibraryOnly = async () => {
    setServicePickerOpen(false);
    if (!pendingSongData) return;
    try {
      const result = await customFetch.post(API_ENDPOINTS.SONG_SUGGESTIONS.ADD_TO_LIBRARY, { songData: pendingSongData });
      toast.success(
        result.data?.isNew
          ? t('songSuggestions.addedNew', 'Song added to your library!')
          : t('songSuggestions.alreadyExists', 'Song was already in your library.')
      );
    } catch (err) {
      toast.error('Failed to add song');
    }
    setPendingSongData(null);
  };

  const handleAddToService = async (serviceId) => {
    setServicePickerOpen(false);
    if (!pendingSongData) return;
    try {
      const result = await customFetch.post(API_ENDPOINTS.SONG_SUGGESTIONS.ADD_TO_LIBRARY, {
        songData: pendingSongData,
        serviceId,
      });
      toast.success(
        result.data?.isNew
          ? t('songSuggestions.addedToService', 'Song added to library and service!')
          : t('songSuggestions.addedToServiceExisting', 'Song added to service!')
      );
    } catch (err) {
      toast.error('Failed to add song');
    }
    setPendingSongData(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate('/app')}>
          <ArrowBackIcon />
        </IconButton>
        <PageHeader
          title={t('songSuggestions.title', 'Song Suggestions')}
          subtitle={t('songSuggestions.subtitle', 'Discover trending and recommended songs')}
          icon={<MusicNoteIcon />}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Trendy Songs Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('songSuggestions.trendySongs', 'Trending Songs')}
          </Typography>
        </Box>
        {trendy.length === 0 ? (
          <Typography color="text.secondary">
            {t('songSuggestions.noTrendy', 'No trending songs available at the moment.')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {trendy.map((song) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={song._id}>
                <SongCard
                  song={song}
                  onAdd={handleAddToLibrary}
                  adding={addingId === song._id}
                  canAdd={canAdd}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Recommended Songs Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StarIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('songSuggestions.recommendedSongs', 'Recommended Songs')}
          </Typography>
        </Box>
        {recommended.length === 0 ? (
          <Typography color="text.secondary">
            {t('songSuggestions.noRecommended', 'No recommended songs available at the moment.')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {recommended.map((song) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={song._id}>
                <SongCard
                  song={song}
                  onAdd={handleAddToLibrary}
                  adding={addingId === song._id}
                  canAdd={canAdd}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Service Picker Dialog */}
      <Dialog open={servicePickerOpen} onClose={() => setServicePickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('songSuggestions.addToService', 'Add to a Service?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('songSuggestions.addToServiceDesc', 'This song will be added to your library. Do you also want to add it to a service?')}
          </Typography>
          <List>
            {futureServices.map((service) => (
              <ListItem key={service._id} disablePadding>
                <ListItemButton onClick={() => handleAddToService(service._id)}>
                  <ListItemText
                    primary={service.title}
                    secondary={new Date(service.date).toLocaleDateString()}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServicePickerOpen(false)} color="inherit">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleAddToLibraryOnly} variant="contained">
            {t('songSuggestions.libraryOnly', 'Library Only')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SongSuggestions;
